import { API_BASE_URL, deleteRequest, get, postJson, put } from './apiClient';

export interface VendorPurchaseOrderItemDto {
  lineNumber?: number;
  itemName: string;
  itemDescription?: string;
  itemCode?: string;
  quantity: number;
  uom?: string;
  unitPrice?: number | null;
  totalPrice?: number | null;
  currency?: string;
  unitPriceUsd?: number | null;
  totalPriceUsd?: number | null;
  remarks?: string;
}

export interface VendorPurchaseOrderVendorDto {
  _id: string;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
}

export interface ConfirmedOrderRef {
  _id: string;
  rfqNumber?: string;
  quotationNumber?: string;
  vesselName?: string;
  clientName?: string;
  confirmedAt?: string;
}

export interface VendorPurchaseOrderDto {
  _id: string;
  poNumber: string;
  /** Original vessel name used in the PO number prefix; preserved for strip/restore. */
  vesselName?: string;
  /** When true, hide the vessel prefix in display, CSV export, and outbound emails. */
  hideVesselPrefix?: boolean;
  confirmedOrderId?: ConfirmedOrderRef | string;
  rfqId?: { _id: string; rfqId?: string; customerName?: string; vesselName?: string } | string;
  quotationId?: string | { _id?: string; quotationNumber?: string };
  vendorId?: VendorPurchaseOrderVendorDto | string | null;
  vendorName: string;
  vendorEmail?: string;
  vendorAddress?: string;
  vendorGstin?: string;
  vendorVat?: string;
  vendorPhone?: string;
  totalItems: number;
  totalPrice: number;
  currency?: string;
  /** INR per 1 AED when PO was generated */
  exchangeRateUsdToTtd?: number | null;
  status: string;
  createdAt: string;
  items: VendorPurchaseOrderItemDto[];
  sentAt?: string;
  sentBy?: string;
  sentTo?: string[];
}

export interface VendorPurchaseOrderListResponse {
  success: boolean;
  data: {
    vendorPurchaseOrders: VendorPurchaseOrderDto[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export async function fetchVendorPurchaseOrders(page = 1, limit = 100, search = '', lean = false) {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) query.set('search', search);
  if (lean) query.set('lean', '1'); // drop items[] — used by the dashboard board
  const res = await get<VendorPurchaseOrderListResponse>(`/vendor-purchase-orders?${query.toString()}`);
  return res.data;
}

export async function fetchVendorPurchaseOrderById(id: string) {
  const res = await get<{ success: boolean; data: VendorPurchaseOrderDto }>(`/vendor-purchase-orders/${id}`);
  return res.data;
}

export async function deleteVendorPurchaseOrder(id: string) {
  const res = await deleteRequest<{ success: boolean; message?: string }>(`/vendor-purchase-orders/${id}`);
  return res;
}

export async function createVendorPurchaseOrdersFromConfirmed(
  confirmedOrderId: string,
  options?: { regenerate?: boolean }
) {
  const q = options?.regenerate ? '?regenerate=1' : '';
  const res = await postJson<{ success: boolean; data: VendorPurchaseOrderDto[] }>(
    `/vendor-purchase-orders/from-confirmed/${confirmedOrderId}${q}`,
    {}
  );
  return res.data;
}

export interface UpdateVendorPurchaseOrderPayload {
  poNumber: string;
  vesselName?: string;
  hideVesselPrefix?: boolean;
  vendorName: string;
  vendorEmail?: string;
  vendorAddress?: string;
  vendorGstin?: string;
  vendorVat?: string;
  vendorPhone?: string;
  createdAt?: string;
  items: Array<{
    lineNumber?: number;
    itemName: string;
    itemDescription?: string;
    itemCode?: string;
    quantity: number | null;
    uom?: string;
    unitPrice: number | null;
    totalPrice: number | null;
    currency?: string;
    unitPriceUsd?: number | null;
    totalPriceUsd?: number | null;
    remarks?: string;
  }>;
}

export async function updateVendorPurchaseOrder(id: string, payload: UpdateVendorPurchaseOrderPayload) {
  const res = await put<{ success: boolean; data: VendorPurchaseOrderDto }>(`/vendor-purchase-orders/${id}`, payload);
  return res.data;
}

export interface SendVendorPurchaseOrderPayload {
  recipients: string[];
  subject: string;
  message: string;
  format: 'pdf' | 'excel';
}

export async function sendVendorPurchaseOrder(id: string, payload: SendVendorPurchaseOrderPayload) {
  const res = await postJson<{ success: boolean; data: VendorPurchaseOrderDto }>(
    `/vendor-purchase-orders/${id}/send`,
    payload,
  );
  return res.data;
}

export interface BatchSendPayload {
  poIds: string[];
  format: 'pdf' | 'excel';
  subject?: string;
  message?: string;
  resend?: boolean;
  recipients?: string[];
}
export interface BatchSendResult {
  sent: { id: string; poNumber?: string; to?: string }[];
  skipped: { id: string; poNumber?: string; reason?: string }[];
  failed: { id: string; poNumber?: string; reason?: string }[];
}
export async function sendVendorPurchaseOrdersBatch(payload: BatchSendPayload) {
  const res = await postJson<{ success: boolean; result: BatchSendResult }>(
    `/vendor-purchase-orders/send-batch`,
    payload,
  );
  return res.result;
}

export interface VendorPoSummaryItem {
  id: string;
  poNumber: string;
  totalPrice: number | null;
  status: string;
  sentAt?: string | null;
}

export interface VendorPoSummary {
  vendorId: string | null;
  vendorName: string;
  vendorEmail: string;
  total: number;
  unsentCount: number;
  unsentPoIds: string[];
  sentCount: number;
  pos: VendorPoSummaryItem[];
}
export async function fetchVendorPoVendorSummary() {
  const res = await get<{ success: boolean; data: VendorPoSummary[] }>(
    `/vendor-purchase-orders/vendor-summary`,
  );
  return res.data;
}

export async function previewVendorPurchaseOrders(poIds: string[], format: 'pdf' | 'excel'): Promise<Blob> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}/vendor-purchase-orders/preview`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ poIds, format }),
  });
  if (!res.ok) throw new Error('Failed to generate preview');
  return res.blob();
}
